import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import {
  getMemberInvolvements,
  getMinistryAreasMale,
  getMinistryAreasFemale,
  updateMemberInvolvements,
  updateMember,
} from '../api';

function FormCompletionInfo({ formCompletedAt, eformExpiresYears, onUpdate, loading }) {
  if (!formCompletedAt) return null;
  const completedDate = new Date(formCompletedAt);
  const now = new Date();
  const yearsAgo = (now - completedDate) / (365.25 * 24 * 60 * 60 * 1000);
  const expiresYears = eformExpiresYears ?? 3;
  const isStale = yearsAgo >= expiresYears;
  const isOutOfDate = yearsAgo >= 1 && !isStale;
  const formattedDate = completedDate.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary">
        Last time the member filled out the form was on {formattedDate}.
      </Typography>
      {isStale && <Chip label="Stale" color="error" size="small" />}
      {isOutOfDate && <Chip label="Out-of-date" color="warning" size="small" />}
      <Button size="small" variant="text" onClick={onUpdate} disabled={loading}>
        Update?
      </Button>
    </Box>
  );
}

function ActionBar({ onSave, onCancel, loading, formCompletionInfo, excludeCheckbox }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', width: '100%' }}>
      <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={loading}>
        Save
      </Button>
      <Button variant="outlined" onClick={onCancel} disabled={loading}>
        Cancel
      </Button>
      {excludeCheckbox}
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: 0 }}>
        {formCompletionInfo}
      </Box>
    </Box>
  );
}

export default function MemberInvolvementsEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [eformExpiresYears, setEformExpiresYears] = useState(3);
  const [ministryAreas, setMinistryAreas] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isInRotationMap, setIsInRotationMap] = useState({});
  const [isExcluded, setIsExcluded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingFormDate, setUpdatingFormDate] = useState(false);
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const involvementsRes = await getMemberInvolvements(id, { pageSize: 500 });
        const memberData = involvementsRes?.member;
        setMember(memberData);
        setEformExpiresYears(involvementsRes?.eform_expires_after_years ?? 3);

        const areasRes = await (memberData?.gender === 'male' ? getMinistryAreasMale() : getMinistryAreasFemale());

        const involvements = involvementsRes?.data ?? [];
        const excluded = Boolean(memberData?.is_excluded);
        const currentIds = excluded
          ? new Set()
          : new Set(
              involvements.map((inv) => inv.ministry_area_id ?? inv.ministry_area?.id).filter(Boolean)
            );
        const inRotationMap = {};
        involvements.forEach((inv) => {
          const maId = inv.ministry_area_id ?? inv.ministry_area?.id;
          if (maId != null) {
            inRotationMap[maId] = inv.is_in_rotation !== false;
          }
        });
        setSelectedIds(currentIds);
        setIsInRotationMap(inRotationMap);
        setIsExcluded(excluded);

        const areas = Array.isArray(areasRes) ? areasRes : [];
        setMinistryAreas(areas);
      } catch (err) {
        console.error(err);
        setSnackbar({ open: true, message: err.message || 'Failed to load data', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const groups = useMemo(() => {
    const byMinistry = {};
    ministryAreas.forEach((ma) => {
      const ministryName = ma.ministry?.name ?? 'Unknown';
      if (!byMinistry[ministryName]) {
        byMinistry[ministryName] = { ministry: ministryName, ministryAreas: [] };
      }
      byMinistry[ministryName].ministryAreas.push({
        id: ma.id,
        name: ma.name,
        is_active: ma.is_active !== false,
        default_in_rotation_value: ma.default_in_rotation_value !== false,
      });
    });
    return Object.values(byMinistry);
  }, [ministryAreas]);

  const columns = useMemo(() => {
    const cols = [[], [], []];
    groups.forEach((g, i) => cols[i % 3].push(g));
    return cols;
  }, [groups]);

  const getIsInRotation = (maId, defaultVal = true) =>
    isInRotationMap[maId] ?? defaultVal;

  const handleToggle = (ministryAreaId, defaultInRotation = true) => {
    if (isExcluded) return;
    if (selectedIds.has(ministryAreaId)) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(ministryAreaId);
        return next;
      });
    } else {
      setIsInRotationMap((m) => ({ ...m, [ministryAreaId]: defaultInRotation }));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.add(ministryAreaId);
        return next;
      });
    }
  };

  const handleInRotationChange = (ministryAreaId, checked) => {
    setIsInRotationMap((prev) => ({ ...prev, [ministryAreaId]: checked }));
  };

  const handleExcludedChange = (event) => {
    const checked = event.target.checked;
    setIsExcluded(checked);
    if (checked) {
      setSelectedIds(new Set());
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (isExcluded) {
        await updateMemberInvolvements(id, []);
        await updateMember(id, { is_excluded: true });
      } else {
        const ministryAreasPayload = Array.from(selectedIds).map((maId) => ({
          id: maId,
          is_in_rotation: getIsInRotation(maId, true),
        }));
        await updateMemberInvolvements(id, ministryAreasPayload);
        await updateMember(id, { is_excluded: false });
      }
      setSnackbar({ open: true, message: 'Involvements saved successfully', severity: 'success' });
      navigate(`/admin/members/${id}/preferences`, { replace: true });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to save involvements', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/admin/members/${id}/preferences`);
  };

  const handleUpdateFormDate = () => {
    setConfirmOverride(true);
  };

  const handleConfirmOverride = async () => {
    try {
      setUpdatingFormDate(true);
      const updated = await updateMember(id, { form_completed_at: new Date().toISOString() });
      setMember((m) => (m ? { ...m, form_completed_at: updated.form_completed_at } : m));
      setSnackbar({ open: true, message: 'Form completion date updated successfully', severity: 'success' });
      setConfirmOverride(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to update form date', severity: 'error' });
    } finally {
      setUpdatingFormDate(false);
    }
  };

  if (loading || !member) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/admin/members/${id}/preferences`)}>
          Back to Involvements
        </Button>
        <Typography sx={{ mt: 2 }}>{loading ? 'Loading...' : 'Member not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/admin/members/${id}/preferences`)}>
        Back to Involvements
      </Button>
      <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
        Edit Involvements for {member.first_name} {member.last_name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {member.email} · {member.gender}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <ActionBar
          onSave={handleSave}
          onCancel={handleCancel}
          loading={saving}
          excludeCheckbox={
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExcluded}
                  onChange={handleExcludedChange}
                  size="small"
                />
              }
              label="Excluded from completing the form"
              sx={{ alignItems: 'center', m: 0 }}
            />
          }
          formCompletionInfo={
            <FormCompletionInfo
              formCompletedAt={member.form_completed_at}
              eformExpiresYears={eformExpiresYears}
              onUpdate={handleUpdateFormDate}
              loading={updatingFormDate}
            />
          }
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        {columns.map((colGroups, colIndex) => (
          <Box key={colIndex} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {colGroups.map((group) => (
              <Card key={group.ministry}>
                <CardContent>
                  <Typography variant="h6" color="primary" gutterBottom>
                    {group.ministry}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {group.ministryAreas.map((ma) =>
                      ma.is_active ? (
                        <Box key={ma.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedIds.has(ma.id)}
                                onChange={() => handleToggle(ma.id, ma.default_in_rotation_value)}
                                size="small"
                                disabled={isExcluded}
                              />
                            }
                            label={ma.name}
                            labelPlacement="end"
                            sx={{ alignItems: 'center', m: 0, flex: 1, minWidth: 0 }}
                          />
                          {selectedIds.has(ma.id) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={getIsInRotation(ma.id, ma.default_in_rotation_value)}
                                  onChange={(e) => handleInRotationChange(ma.id, e.target.checked)}
                                  size="small"
                                  disabled={isExcluded}
                                />
                              }
                              label="In Rotation?"
                              labelPlacement="end"
                              sx={{ alignItems: 'center', m: 0, flexShrink: 0 }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Box key={ma.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <Checkbox size="small" disabled checked={selectedIds.has(ma.id)} />
                          <Typography variant="body2" component="span" color="text.secondary">
                            {ma.name}
                          </Typography>
                          {selectedIds.has(ma.id) && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  disabled
                                  checked={getIsInRotation(ma.id, ma.default_in_rotation_value)}
                                />
                              }
                              label="In Rotation?"
                              labelPlacement="end"
                              sx={{ alignItems: 'center', m: 0, flexShrink: 0 }}
                            />
                          )}
                          <Chip label="Disabled" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                      )
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ))}
      </Box>

      <Box>
        <ActionBar
          onSave={handleSave}
          onCancel={handleCancel}
          loading={saving}
          excludeCheckbox={
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExcluded}
                  onChange={handleExcludedChange}
                  size="small"
                />
              }
              label="Excluded from completing the form"
              sx={{ alignItems: 'center', m: 0 }}
            />
          }
          formCompletionInfo={
            <FormCompletionInfo
              formCompletedAt={member.form_completed_at}
              eformExpiresYears={eformExpiresYears}
              onUpdate={handleUpdateFormDate}
              loading={updatingFormDate}
            />
          }
        />
      </Box>

      <Dialog open={confirmOverride} onClose={() => !updatingFormDate && setConfirmOverride(false)}>
        <DialogTitle>Update Form Completion Date</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to manually override the member's form completion date? This will bring the completion
            date up-to-date and the member will fall off of any stale or out of date lists.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverride(false)} disabled={updatingFormDate}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmOverride} disabled={updatingFormDate}>
            {updatingFormDate ? 'Updating…' : 'Yes, update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
